import { z } from 'zod';

const optionalUrl = z.union([z.string().url('Enter a full URL'), z.literal('')]);
const optionalText = (max: number) => z.string().trim().max(max).optional().or(z.literal(''));

export const heroSchema = z.object({
  portraitUrl: z.string().optional().or(z.literal('')),
  tagline: z.string().trim().max(60).optional().or(z.literal('')),
  headline: z.string().trim().min(3, 'Headline is required').max(140),
  subheadline: z.string().trim().max(300),
  ctaLabel: z.string().trim().max(32).optional().or(z.literal('')),
  ctaHref: optionalUrl,
  availability: z.enum(['available', 'limited', 'closed']).default('available'),
});
export type HeroValues = z.infer<typeof heroSchema>;

export const aboutSchema = z.object({
  body: z.string().trim().min(1).max(1200),
  skills: z.array(z.string().trim().min(1)).max(20),
  resumeUrl: optionalUrl,
});
export type AboutValues = z.infer<typeof aboutSchema>;

export const MEDIA_KINDS = ['none', 'image', 'video'] as const;
export type ProjectMediaKind = (typeof MEDIA_KINDS)[number];

export const projectsSchema = z.object({
  items: z.array(
    z
      .object({
        id: z.string(),
        title: z.string().trim().min(1, 'Title is required'),
        summary: z.string().trim().max(400),
        role: optionalText(80),
        client: optionalText(80),
        year: z.string().trim().regex(/^\d{4}$/, 'YYYY'),
        url: optionalUrl,
        mediaKind: z.enum(MEDIA_KINDS).default('none'),
        mediaUrl: optionalUrl,
        mediaAlt: optionalText(160),
        tags: z.array(z.string().trim().min(1)).max(10).default([]),
        bullets: z.array(z.string().trim().min(1).max(240)).max(8).default([]),
      })
      .superRefine((item, ctx) => {
        if (item.mediaKind !== 'none' && !item.mediaUrl) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mediaUrl'],
            message: 'Add a URL or set media to None',
          });
        }
      }),
  ),
});
export type ProjectsValues = z.infer<typeof projectsSchema>;

export const experienceSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      company: z.string().trim().min(1),
      role: z.string().trim().min(1),
      location: optionalText(80),
      start: z.string().trim().min(1),
      end: z.string().trim().min(1),
      current: z.boolean().default(false),
      summary: z.string().trim().max(500),
      highlights: z.array(z.string().trim().min(1).max(240)).max(8).default([]),
    }),
  ),
});
export type ExperienceValues = z.infer<typeof experienceSchema>;

export const educationSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      institution: z.string().trim().min(1),
      credential: z.string().trim().min(1),
      startYear: z.string().trim().regex(/^\d{4}$/, 'YYYY'),
      endYear: z.string().trim().regex(/^\d{4}$|^$/, 'YYYY or empty'),
      gpa: z
        .string()
        .trim()
        .regex(/^(\d(\.\d{1,2})?|)$/u, 'e.g. 3.8')
        .optional()
        .or(z.literal('')),
      note: optionalText(240),
    }),
  ),
});
export type EducationValues = z.infer<typeof educationSchema>;

export const LINK_PLATFORMS = [
  'website',
  'email',
  'github',
  'gitlab',
  'linkedin',
  'twitter',
  'bluesky',
  'mastodon',
  'instagram',
  'dribbble',
  'behance',
  'figma',
  'youtube',
  'vimeo',
  'medium',
  'substack',
  'spotify',
  'custom',
] as const;
export type LinkPlatform = (typeof LINK_PLATFORMS)[number];

const phoneField = z
  .string()
  .trim()
  .max(32)
  .regex(/^[+\d][\d\s\-().]*$/u, 'Digits, spaces, and + - ( ) only')
  .optional()
  .or(z.literal(''));

export const contactSchema = z.object({
  email: z.union([z.string().email('Invalid email'), z.literal('')]),
  phone: phoneField,
  location: optionalText(80),
  links: z.array(
    z
      .object({
        id: z.string(),
        platform: z.enum(LINK_PLATFORMS).default('custom'),
        label: z.string().trim().max(40),
        href: z.string().trim().min(1, 'Required'),
      })
      .superRefine((link, ctx) => {
        if (link.platform === 'email') {
          const ok = z.string().email().safeParse(link.href).success;
          if (!ok) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['href'], message: 'Enter an email address' });
          }
          return;
        }
        const ok = z.string().url().safeParse(link.href).success;
        if (!ok) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['href'], message: 'Enter a full URL' });
        }
      }),
  ),
  allowInquiryForm: z.boolean(),
});
export type ContactValues = z.infer<typeof contactSchema>;
